import React, { useState } from "react";
import {
  Sparkles, Flame, Lightbulb, AlertTriangle, Bot, Search, Bell, MessageSquare, ListTodo,
  Mail, Phone, Calendar, TrendingUp, Check, Star, Building2, Zap, Send, Target,
} from "lucide-react";

// macOS-style browser frame with an app top bar (search + alerts/messages/tasks tools).
export const BrowserFrame = ({ children, url = "app.aurelo.com" }) => (
  <div className="rounded-2xl border border-slate-200/70 bg-white shadow-[0_30px_80px_rgba(11,16,43,0.18)] overflow-hidden">
    <div className="flex items-center gap-2 px-4 h-10 bg-slate-50 border-b border-slate-200/70">
      <span className="h-3 w-3 rounded-full bg-[#FF5F57]" /><span className="h-3 w-3 rounded-full bg-[#FEBC2E]" /><span className="h-3 w-3 rounded-full bg-[#28C840]" />
      <div className="ml-3 flex-1 max-w-xs h-6 rounded-md bg-white border border-slate-200 text-[11px] text-slate-400 flex items-center px-3">{url}</div>
    </div>
    <div className="bg-[#F1F5F9]">{children}</div>
  </div>
);

const NAV = ["Home", "Leads", "Contacts", "Deals", "Pipeline", "Campaigns", "Agents", "AI Copilot", "Analytics"];
const MiniSidebar = ({ active }) => (
  <div className="w-40 bg-[#0B102B] p-3 hidden sm:block shrink-0">
    <div className="flex items-center gap-2 mb-4 px-1"><img src="/brand/aurelo_logo_white_v2.png" alt="Aurelo" className="h-4" /></div>
    {NAV.map((x) => (
      <div key={x} className={`text-[11px] rounded-lg px-2.5 py-1.5 mb-0.5 ${x === active ? "bg-white/10 text-white" : "text-slate-400"}`}>{x}</div>
    ))}
  </div>
);

// In-app top bar shown inside every preview — alerts, messages, tasks tools.
const MiniTopBar = ({ title }) => (
  <div className="flex items-center gap-3 px-4 h-11 bg-white border-b border-slate-200">
    <div className="text-[13px] font-bold text-slate-900">{title}</div>
    <div className="ml-2 hidden md:flex items-center gap-1.5 h-6 w-44 rounded-lg bg-slate-50 border border-slate-200 px-2 text-[10px] text-slate-400"><Search className="h-3 w-3" />Search or ask AI…</div>
    <div className="ml-auto flex items-center gap-1.5">
      <span className="inline-flex items-center gap-1 h-6 rounded-md aurelo-gradient text-white text-[10px] font-semibold px-2"><Sparkles className="h-3 w-3" />AI</span>
      {[[MessageSquare, 6, "#7C3AED"], [ListTodo, 4, "#0EA5E9"], [Bell, 3, "#EF4444"]].map(([Icon, n, c], i) => (
        <span key={i} className="relative h-6 w-6 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500">
          <Icon className="h-3.5 w-3.5" />
          <span className="absolute -top-1 -right-1 h-3 min-w-3 px-0.5 rounded-full text-white text-[8px] font-bold flex items-center justify-center" style={{ background: c }}>{n}</span>
        </span>
      ))}
      <span className="h-6 w-6 rounded-full bg-gradient-to-br from-violet-400 to-blue-500" />
    </div>
  </div>
);

const Screen = ({ active, title, children }) => (
  <div className="flex">
    <MiniSidebar active={active} />
    <div className="flex-1 min-w-0">
      <MiniTopBar title={title} />
      <div className="p-4">{children}</div>
    </div>
  </div>
);

const Card = ({ children, className = "" }) => <div className={`rounded-xl bg-white border border-slate-200 p-3 ${className}`}>{children}</div>;

const Dashboard = () => (
  <Screen active="Home" title="Good morning, Ernesto 👋">
    <div className="grid grid-cols-4 gap-2.5">
      {[["Pipeline", "$2.4M", "+18%"], ["Open Deals", "128", "+12%"], ["New Leads", "356", "+24%"], ["Conversion", "26%", "+6%"]].map(([l, v, d]) => (
        <Card key={l}><div className="text-[10px] text-slate-500">{l}</div><div className="text-base font-bold text-slate-900 tabular-nums">{v}</div><div className="text-[10px] text-emerald-600 font-semibold">{d}</div></Card>
      ))}
    </div>
    <div className="grid grid-cols-3 gap-2.5 mt-2.5">
      <Card><div className="text-xs font-semibold">Sales Pipeline</div>
        {[["Leads", "92%", "#7C3AED"], ["Qualified", "64%", "#3B82F6"], ["Opportunities", "40%", "#06B6D4"], ["Won", "18%", "#10B981"]].map(([s, w, c]) => (
          <div key={s} className="flex items-center gap-2 text-[10px] mt-1.5"><span className="w-16 text-slate-500">{s}</span><div className="flex-1 h-1.5 rounded-full bg-slate-100"><div className="h-1.5 rounded-full" style={{ width: w, background: c }} /></div></div>
        ))}
      </Card>
      <Card><div className="flex items-center gap-1 text-xs font-semibold text-[#7C3AED]"><Sparkles className="h-3.5 w-3.5" />AI Insights</div>
        <div className="mt-2 space-y-1.5 text-[10px] text-slate-600">
          <div className="flex gap-1.5"><Flame className="h-3 w-3 text-orange-500 shrink-0" />14 high-intent leads need follow-up</div>
          <div className="flex gap-1.5"><Lightbulb className="h-3 w-3 text-amber-500 shrink-0" />ACME shows buying signals</div>
          <div className="flex gap-1.5"><AlertTriangle className="h-3 w-3 text-red-500 shrink-0" />3 deals at risk</div>
        </div>
      </Card>
      <Card><div className="flex items-center gap-1 text-xs font-semibold text-slate-800"><ListTodo className="h-3.5 w-3.5 text-[#0EA5E9]" />Today's Tasks</div>
        <div className="mt-2 space-y-1.5 text-[10px]">
          {[["Call ACME Corp", "bg-red-500"], ["Send proposal · GlobalTech", "bg-amber-500"], ["Demo · Vision Labs", "bg-slate-300"]].map(([t, c]) => (
            <div key={t} className="flex items-center gap-1.5"><span className="h-3 w-3 rounded border border-slate-300" /><span className={`h-1.5 w-1.5 rounded-full ${c}`} /><span className="text-slate-600 truncate">{t}</span></div>
          ))}
        </div>
      </Card>
    </div>
  </Screen>
);

const STAGES = [["New Lead", ["ACME", "Innova"]], ["Qualified", ["GlobalTech", "Vision"]], ["Proposal", ["Bright", "Apex"]], ["Negotiation", ["FutureWorks"]]];
const Pipeline = () => (
  <Screen active="Pipeline" title="Sales Pipeline">
    <div className="flex gap-2.5 overflow-hidden">
      {STAGES.map(([name, deals], i) => (
        <div key={name} className="w-36 shrink-0 rounded-xl bg-slate-100/70 border border-slate-200 p-2">
          <div className="flex items-center justify-between mb-2 px-1"><span className="text-[11px] font-semibold text-slate-700">{name}</span><span className="text-[9px] text-slate-400">{deals.length}</span></div>
          {deals.map((d, j) => (
            <div key={j} className="rounded-lg bg-white border border-slate-200 p-2 mb-1.5"><div className="text-[11px] font-semibold text-slate-900">{d} Corp</div><div className="flex justify-between mt-1"><span className="text-[11px] font-bold">${[35, 75, 120, 185][(i + j) % 4]}K</span><span className="text-[9px] text-slate-400">{[45, 60, 72, 80][(i + j) % 4]}%</span></div><div className="h-1 rounded-full bg-slate-100 mt-1"><div className="h-1 rounded-full aurelo-gradient" style={{ width: `${[45, 60, 72, 80][(i + j) % 4]}%` }} /></div></div>
          ))}
        </div>
      ))}
    </div>
  </Screen>
);

const Copilot = () => (
  <Screen active="AI Copilot" title="Aurelo AI Copilot">
    <div className="space-y-2">
      <div className="rounded-xl aurelo-gradient text-white text-[11px] px-3 py-2 ml-auto max-w-[70%]">Which deals are most likely to close this month?</div>
      <div className="rounded-xl bg-white border border-slate-200 text-[11px] text-slate-700 px-3 py-2.5 max-w-[85%]">
        <div className="flex items-center gap-1 text-[10px] font-semibold text-[#7C3AED] mb-1"><Bot className="h-3 w-3" />Aurelo AI</div>
        3 deals show strong momentum: <b>ACME</b> ($185K, 72%), <b>GlobalTech</b> ($120K, 68%) and <b>Vision Labs</b> ($95K, 76%). Prioritize Vision Labs — highest probability & fastest cycle.
        <div className="flex gap-1.5 mt-2">{["View Deals", "Draft Follow-ups"].map((a) => <span key={a} className="text-[10px] rounded-md border border-[#7C3AED]/30 text-[#7C3AED] px-1.5 py-0.5">{a}</span>)}</div>
      </div>
    </div>
  </Screen>
);

const Analytics = () => (
  <Screen active="Analytics" title="Revenue Analytics">
    <div className="grid grid-cols-4 gap-2 mb-3">{[["Win Rate", "58%"], ["Avg Deal", "$92K"], ["ARR", "$9.1M"], ["Cycle", "34d"]].map(([l, v]) => <Card key={l} className="p-2"><div className="text-[9px] text-slate-500">{l}</div><div className="text-sm font-bold tabular-nums">{v}</div></Card>)}</div>
    <Card><div className="text-[11px] font-semibold mb-2">Pipeline by Stage</div>
      <div className="flex items-end gap-2 h-24">{[40, 65, 52, 78, 60, 90, 48].map((h, i) => <div key={i} className="flex-1 rounded-t aurelo-gradient" style={{ height: `${h}%`, opacity: 0.5 + i * 0.07 }} />)}</div>
    </Card>
  </Screen>
);

// --- NEW previews ---
const Contact360 = () => (
  <Screen active="Contacts" title="Contact 360">
    <div className="grid grid-cols-3 gap-2.5">
      <Card className="col-span-1">
        <div className="flex items-center gap-2"><span className="h-10 w-10 rounded-full bg-gradient-to-br from-violet-400 to-blue-500" /><div><div className="text-xs font-bold text-slate-900">Sarah Johnson</div><div className="text-[10px] text-slate-500">CTO · ACME Corp</div></div></div>
        <div className="mt-2 flex items-center justify-between rounded-lg bg-violet-50 px-2 py-1.5"><span className="text-[10px] font-semibold text-[#7C3AED]">AI Lead Score</span><span className="text-sm font-bold text-[#7C3AED]">92</span></div>
        <div className="mt-2 grid grid-cols-3 gap-1">{[Mail, Phone, Calendar].map((I, i) => <div key={i} className="h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500"><I className="h-3.5 w-3.5" /></div>)}</div>
        <div className="mt-2 flex flex-wrap gap-1">{["enterprise", "champion", "hot"].map((t) => <span key={t} className="text-[9px] rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5">{t}</span>)}</div>
      </Card>
      <Card className="col-span-2">
        <div className="text-[11px] font-semibold mb-2">Activity Timeline</div>
        {[[Mail, "Opened proposal email", "text-blue-500"], [TrendingUp, "AI raised score 78 → 92", "text-violet-500"], [Calendar, "Booked security review", "text-emerald-500"], [Phone, "Discovery call · 32 min", "text-amber-500"]].map(([I, t, c], i) => (
          <div key={i} className="flex items-center gap-2 text-[10px] py-1 border-b border-slate-50 last:border-0"><I className={`h-3.5 w-3.5 ${c} shrink-0`} /><span className="text-slate-600 flex-1 truncate">{t}</span><span className="text-slate-300">{i + 1}d</span></div>
        ))}
        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-2 text-[10px] text-slate-600"><span className="font-semibold text-[#7C3AED]">AI brief:</span> Strong champion. Security is the last blocker — send SOC2 pack.</div>
      </Card>
    </div>
  </Screen>
);

const DealWorkspace = () => (
  <Screen active="Deals" title="Deal Workspace">
    <div className="flex items-center justify-between mb-2.5">
      <div><div className="text-xs font-bold text-slate-900">ACME · Enterprise Platform</div><div className="text-[10px] text-slate-500">$185,000 · Negotiation · 72%</div></div>
      <span className="text-[10px] rounded-full bg-amber-50 text-amber-600 font-semibold px-2 py-1 flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Medium risk</span>
    </div>
    <div className="flex gap-1 mb-2.5">{["New", "Qualified", "Demo", "Proposal", "Negotiation", "Won"].map((s, i) => <div key={s} className={`flex-1 h-1.5 rounded-full ${i <= 4 ? "aurelo-gradient" : "bg-slate-200"}`} />)}</div>
    <div className="grid grid-cols-3 gap-2.5">
      <Card><div className="flex items-center gap-1 text-[11px] font-semibold text-[#7C3AED]"><Sparkles className="h-3.5 w-3.5" />Deal Health</div><div className="mt-1.5 text-2xl font-bold text-slate-900">72<span className="text-xs text-slate-400">/100</span></div><div className="text-[10px] text-slate-500">Momentum steady</div></Card>
      <Card className="col-span-2"><div className="text-[11px] font-semibold mb-1.5">AI Next Best Actions</div>{["Send SOC2 & data-residency docs", "Loop in economic buyer (CFO)", "Propose 2-year term for discount"].map((t) => <div key={t} className="flex items-center gap-1.5 text-[10px] text-slate-600 py-0.5"><Check className="h-3 w-3 text-emerald-500 shrink-0" />{t}</div>)}</Card>
    </div>
  </Screen>
);

const Campaigns = () => (
  <Screen active="Campaigns" title="Marketing Campaigns">
    <div className="grid grid-cols-4 gap-2 mb-2.5">{[["Sent", "4,820"], ["Opened", "2,510"], ["Clicked", "742"], ["Revenue", "$410K"]].map(([l, v]) => <Card key={l} className="p-2"><div className="text-[9px] text-slate-500">{l}</div><div className="text-sm font-bold tabular-nums">{v}</div></Card>)}</div>
    <Card>
      <div className="text-[11px] font-semibold mb-2">Active Campaigns</div>
      {[["Q3 Healthcare Enterprise", "active", "#10B981", "62% open"], ["Product Launch Webinar", "scheduled", "#0EA5E9", "312 RSVPs"], ["Renewal Reminder", "completed", "#94A3B8", "$240K infl."]].map(([n, s, c, m]) => (
        <div key={n} className="flex items-center gap-2 py-1.5 border-b border-slate-50 last:border-0">
          <span className="h-6 w-6 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-500"><Send className="h-3 w-3" /></span>
          <div className="flex-1 min-w-0"><div className="text-[11px] font-semibold text-slate-800 truncate">{n}</div><div className="text-[9px] text-slate-400">{m}</div></div>
          <span className="text-[9px] rounded-full font-semibold px-1.5 py-0.5" style={{ color: c, background: `${c}1a` }}>{s}</span>
        </div>
      ))}
    </Card>
  </Screen>
);

const Agents = () => (
  <Screen active="Agents" title="AI SDR Agents">
    <div className="grid grid-cols-2 gap-2.5">
      {[["Sophia", "Enterprise SDR", "active", "#10B981", 342, 18], ["Atlas", "Account Researcher", "paused", "#94A3B8", 128, 0]].map(([n, r, s, c, act, mtg]) => (
        <Card key={n}>
          <div className="flex items-center gap-2"><span className="h-8 w-8 rounded-lg aurelo-gradient flex items-center justify-center text-white"><Bot className="h-4 w-4" /></span><div className="flex-1"><div className="text-xs font-bold text-slate-900">{n}</div><div className="text-[10px] text-slate-500">{r}</div></div><span className="h-2 w-2 rounded-full" style={{ background: c }} /></div>
          <div className="mt-2 grid grid-cols-2 gap-1.5">
            <div className="rounded-lg bg-slate-50 p-1.5 text-center"><div className="text-sm font-bold text-slate-900">{act}</div><div className="text-[9px] text-slate-400">actions</div></div>
            <div className="rounded-lg bg-slate-50 p-1.5 text-center"><div className="text-sm font-bold text-slate-900">{mtg}</div><div className="text-[9px] text-slate-400">meetings</div></div>
          </div>
        </Card>
      ))}
    </div>
    <Card className="mt-2.5"><div className="flex items-center gap-1 text-[11px] font-semibold text-[#7C3AED] mb-1.5"><Target className="h-3.5 w-3.5" />Sophia · Live activity</div>
      {[["Researched ACME Corp buying signals", Zap], ["Drafted personalized outreach (pending approval)", Mail], ["Booked demo with GlobalTech", Calendar]].map(([t, I], i) => (
        <div key={i} className="flex items-center gap-1.5 text-[10px] text-slate-600 py-0.5"><I className="h-3 w-3 text-slate-400 shrink-0" />{t}</div>
      ))}
    </Card>
  </Screen>
);

const TABS = [
  { key: "dashboard", label: "Revenue Dashboard", node: <Dashboard /> },
  { key: "pipeline", label: "Sales Pipeline", node: <Pipeline /> },
  { key: "contact", label: "Contact 360", node: <Contact360 /> },
  { key: "deal", label: "Deal Workspace", node: <DealWorkspace /> },
  { key: "agents", label: "AI SDR Agents", node: <Agents /> },
  { key: "campaigns", label: "Campaigns", node: <Campaigns /> },
  { key: "copilot", label: "AI Copilot", node: <Copilot /> },
  { key: "analytics", label: "Analytics", node: <Analytics /> },
];

export const AppPreviewTabs = () => {
  const [tab, setTab] = useState("dashboard");
  const active = TABS.find((t) => t.key === tab);
  return (
    <div>
      <div className="flex flex-wrap justify-center gap-2 mb-6">
        {TABS.map((t) => (
          <button key={t.key} data-testid={`preview-tab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-3.5 py-2 rounded-full text-[13px] font-semibold transition-all ${tab === t.key ? "aurelo-gradient text-white shadow-[0_8px_20px_rgba(124,58,237,0.25)]" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="max-w-4xl mx-auto"><BrowserFrame url={`app.aurelo.com/${tab}`}>{active.node}</BrowserFrame></div>
    </div>
  );
};
